import { atomWithStorage } from 'jotai/utils';
import { getMapDefaultConfig } from 'components/map/atoms';
import { mutationAtomWithLoadable } from 'lib/atoms';
import request from 'lib/request';
import { toastAtom as sharedToastAtom } from 'components/scenario-area-configs/atoms';
import type { TaxScenarioResponse, SaveScenarioParams } from './types';

// ─────────────────────────────────────
// Re-export shared area config atoms
// ─────────────────────────────────────

export { sharedToastAtom as toastAtom };

export {
  categoriesBaseAtom,
  categoriesLoadableAtom,
  unitTypesBaseAtom,
  unitTypesLoadableAtom,
  areaCategoryAtom,
  areaQueryAtom,
  areaSearchLoadableAtom,
  unitTypeAtom,
  unitQueryAtom,
  unitSearchLoadableAtom,
  stateQueryAtom,
  stateSearchLoadableAtom,
  areaGeometriesAtom,
  fetchGeometryAtom,
  fetchGeometryLoadableAtom,
} from 'components/scenario-area-configs/atoms';

// ─────────────────────────────────────
// Map config — scoped to Tax feature
// ─────────────────────────────────────

export const taxMapConfigAtom = atomWithStorage(
  'insights-tax-scenario-map-config',
  getMapDefaultConfig({
    wellsV2: {
      checked: false,
      options: {
        surfaceHole: false,
        bottomHole: false,
        stick: false,
        survey: false,
        vertical: false,
        horizontal: false,
        horizontalPermit: false,
        horizontalDuc: false,
        horizontalProducing: false,
        horizontalShutIn: false,
        horizontalExpiredPermit: false,
        horizontalInactive: false,
        horizontalPrePermit: false,
        horizontalApd: false,
        horizontalProposal: false,
        horizontalPlanned: false,
        verticalPermit: false,
        verticalDuc: false,
        verticalProducing: false,
        verticalShutIn: false,
        verticalExpiredPermit: false,
        verticalInactive: false,
        verticalPrePermit: false,
        verticalApd: false,
        verticalProposal: false,
        verticalPlanned: false,
        permit: false,
        duc: false,
        producing: false,
        shutIn: false,
        expiredPermit: false,
        inactive: false,
        prePermit: false,
        apd: false,
        proposal: false,
        planned: false,
      },
    },
    developmentAreas: {
      checked: false,
      options: {
        horizontalUnits: false,
        verticalUnits: false,
        fieldUnits: false,
        pricing: false,
      },
    },
    basinTier: { checked: false, options: { basin: false, tier: false } },
    scrollZoom: { checked: false },
    popups: { checked: false },
  })
);

// ─────────────────────────────────────
// Tax-specific: Save scenario
// ─────────────────────────────────────

export const [saveScenarioAtom, saveScenarioLoadableAtom] =
  mutationAtomWithLoadable(
    async (
      _get,
      _set,
      params: SaveScenarioParams
    ): Promise<TaxScenarioResponse> => {
      const url =
        params.mode === 'create'
          ? '/tax_scenarios'
          : `/tax_scenarios/${params.id}`;

      const method = params.mode === 'create' ? request.post : request.patch;

      const response = await method<TaxScenarioResponse, unknown>(
        url,
        params.payload,
        new AbortController()
      );

      if (response.type !== 'success') {
        const data = response.type === 'error' ? response.data : undefined;
        const body = data as { errors?: { message: string }[] } | undefined;
        const message =
          body?.errors?.map((e) => e.message).join(', ') ||
          'Failed to save scenario';
        throw new Error(message);
      }

      return response.data;
    }
  );

// ─────────────────────────────────────
// Tax-specific: Clone scenario
// ─────────────────────────────────────

export const [cloneScenarioAtom, cloneScenarioLoadableAtom] =
  mutationAtomWithLoadable(
    async (_get, set, id: number): Promise<TaxScenarioResponse> => {
      const response = await request.post<TaxScenarioResponse, unknown>(
        `/tax_scenarios/${id}/clone`,
        {},
        new AbortController()
      );

      if (response.type !== 'success') {
        set(sharedToastAtom, {
          message: 'Failed to clone scenario',
          type: 'error',
        });
        throw new Error('Failed to clone scenario');
      }

      return response.data;
    }
  );

// ─────────────────────────────────────
// Tax-specific: Fetch scenario (edit mode)
// ─────────────────────────────────────

export const [fetchScenarioAtom, fetchScenarioLoadableAtom] =
  mutationAtomWithLoadable(
    async (_get, _set, id: number): Promise<TaxScenarioResponse> => {
      const response = await request.get<TaxScenarioResponse>(
        `/tax_scenarios/${id}`
      );

      if (!response || 'error' in response) {
        throw new Error(
          (response as { error: string })?.error ?? 'Failed to load scenario'
        );
      }

      return response;
    }
  );
